import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { Save, X, MapPin, Calendar, DollarSign, Briefcase, FileText, Loader, AlertCircle } from "lucide-react";
import { initPlaceAutocomplete, destroyPlaceAutocomplete } from '@/utils/placeAutocomplete';

const EditJobModal = ({ job, isOpen, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    job_type: '',
    role: '',
    date: '',
    hours: 0,
    pay_rate: 0,
    total_amount: 0,
    location: '',
    location_address: '',
    location_lat: null,
    location_lng: null,
    status: '',
    requirements: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [loadingJobData, setLoadingJobData] = useState(false);
  const [fullJobData, setFullJobData] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [autocompleteReady, setAutocompleteReady] = useState(false);
  const autocompleteInitialized = useRef(false);
  const addressValueRef = useRef('');
  const containerId = 'edit-job-location-autocomplete';

  // Fetch full job data when modal opens
  useEffect(() => {
    if (job && isOpen) {
      // Reset state when opening
      autocompleteInitialized.current = false;
      setDataLoaded(false);
      setAutocompleteReady(false);
      addressValueRef.current = '';
      fetchFullJobData();
    }
  }, [job, isOpen]);

  const fetchFullJobData = async () => {
    if (!job?.id) return;

    try {
      setLoadingJobData(true);
      // Owner endpoint, not /api/admin/... — the admin guard rejects business owners
      // there and the admin interceptor sent them to the admin login.
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${job.id}/edit`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('shootzu_token')}` } }
      );

      const jobData = response.data.job;
      setFullJobData(jobData);

      console.log('Job data loaded:', jobData);

      // Format date for input (YYYY-MM-DD)
      let formattedDate = '';
      if (jobData.date || jobData.jobDate) {
        const dateStr = jobData.date || jobData.jobDate;
        try {
          const d = new Date(dateStr);
          if (!isNaN(d.getTime())) {
            formattedDate = d.toISOString().split('T')[0];
          }
        } catch (e) {
          formattedDate = dateStr;
        }
      }

      // Get role - check multiple possible field names including crew_members
      let role = jobData.role || jobData.type || jobData.crew_type || '';
      // Check crew_members for role if not found at job level
      if (!role && jobData.crew_members && jobData.crew_members.length > 0) {
        role = jobData.crew_members[0].role || '';
      }
      // Normalize role to lowercase for matching select options
      if (role) {
        role = role.toLowerCase().replace(/\s+/g, '_');
      }

      // Simple jobs store pricing in crew_members[0]
      const firstCrew = jobData.crew_members && jobData.crew_members.length > 0 ? jobData.crew_members[0] : null;
      const hours = parseFloat(firstCrew?.hours) || parseFloat(jobData.hours) || 0;
      const payRate = parseFloat(firstCrew?.ratePerHour || firstCrew?.rate_per_hour) || parseFloat(jobData.pay_rate || jobData.ratePerHour || jobData.hourly_rate) || 0;
      const totalAmount = parseFloat(firstCrew?.subtotal) || parseFloat(jobData.total_amount || jobData.total || jobData.budget) || 0;

      // Get location address
      const locationAddress = jobData.location_address || jobData.locationAddress || jobData.location || '';

      const newFormData = {
        title: jobData.title || '',
        description: jobData.description || jobData.brief || '',
        job_type: jobData.job_type || jobData.category || '',
        role: role,
        date: formattedDate,
        hours: hours,
        pay_rate: payRate,
        total_amount: totalAmount,
        location: jobData.location || '',
        location_address: locationAddress,
        location_lat: jobData.location_lat || jobData.locationLat || jobData.lat || null,
        location_lng: jobData.location_lng || jobData.locationLng || jobData.lng || null,
        status: jobData.status || '',
        requirements: jobData.requirements || jobData.special_requirements || '',
        notes: jobData.notes || jobData.admin_notes || ''
      };

      console.log('Form data set:', newFormData);
      addressValueRef.current = locationAddress;
      setFormData(newFormData);
      setDataLoaded(true);
    } catch (error) {
      console.error('Failed to fetch job details:', error);
      toast.error('Failed to load job details');

      // Fallback to basic job data
      const fallbackData = {
        title: job.title || '',
        description: job.description || job.brief || '',
        job_type: job.job_type || job.category || '',
        role: (job.role || job.type || '').toLowerCase().replace(/\s+/g, '_'),
        date: job.date || job.jobDate || '',
        hours: parseFloat(job.hours) || 0,
        pay_rate: parseFloat(job.pay_rate || job.ratePerHour) || 0,
        total_amount: parseFloat(job.total_amount || job.budget) || 0,
        location: job.location || '',
        location_address: job.location_address || job.location || '',
        location_lat: job.location_lat || null,
        location_lng: job.location_lng || null,
        status: job.status || '',
        requirements: job.requirements || '',
        notes: job.notes || ''
      };
      setFormData(fallbackData);
      setDataLoaded(true);
    } finally {
      setLoadingJobData(false);
    }
  };

  // Initialize Google Places Autocomplete AFTER data is loaded
  useEffect(() => {
    if (isOpen && dataLoaded && !loadingJobData) {
      // Delay to ensure DOM is ready
      const timer = setTimeout(() => {
        initializeAutocomplete();
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [isOpen, dataLoaded, loadingJobData, formData.location_address]);

  // Cleanup on close
  useEffect(() => {
    if (!isOpen && autocompleteInitialized.current) {
      destroyPlaceAutocomplete(containerId);
      autocompleteInitialized.current = false;
    }
  }, [isOpen]);

  const initializeAutocomplete = () => {
    if (autocompleteInitialized.current) return;

    const container = document.getElementById(containerId);
    if (!container) {
      console.log('Container not found, retrying...');
      setTimeout(initializeAutocomplete, 100);
      return;
    }

    // Check if Google Maps is loaded
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.log('Google Maps not loaded, showing fallback input');
      setAutocompleteReady(false);
      return;
    }

    const addressValue = addressValueRef.current || '';
    console.log('Initializing autocomplete with address:', addressValue);

    const result = initPlaceAutocomplete(
      containerId,
      (placeData) => {
        console.log('Place selected:', placeData);
        setFormData(prev => ({
          ...prev,
          location: placeData.formatted_address,
          location_address: placeData.formatted_address,
          location_lat: placeData.lat,
          location_lng: placeData.lng
        }));
      },
      {
        placeholder: 'Start typing address...',
        className: 'w-full px-3 py-2 rounded-[11px] border border-white/15 bg-white/5 text-white placeholder-white/40 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all text-sm'
      },
      addressValue
    );

    if (result) {
      autocompleteInitialized.current = true;
      setAutocompleteReady(true);
      console.log('Autocomplete initialized successfully');
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Job title is required');
      return;
    }

    try {
      setLoading(true);

      // Prepare data for update
      const updateData = {
        title: formData.title,
        description: formData.description,
        job_type: formData.job_type,
        role: formData.role,
        date: formData.date,
        hours: parseFloat(formData.hours) || 0,
        pay_rate: parseFloat(formData.pay_rate) || 0,
        total_amount: parseFloat(formData.total_amount) || 0,
        location: formData.location_address || formData.location,
        location_address: formData.location_address,
        location_lat: formData.location_lat,
        location_lng: formData.location_lng,
        requirements: formData.requirements,
        // Sync crew_members[0] for simple jobs so pricing stays consistent
        crew_members: [{
          role: formData.role,
          hours: parseFloat(formData.hours) || 0,
          ratePerHour: parseFloat(formData.pay_rate) || 0,
          subtotal: parseFloat(formData.total_amount) || 0
        }]
      };

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${job.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('shootzu_token')}` } }
      );

      toast.success('Job updated successfully');
      onUpdate();
      handleClose();
    } catch (error) {
      console.error('Update job error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update job');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      // Auto-calculate total if rate or hours change
      if (field === 'pay_rate' || field === 'hours') {
        const rate = field === 'pay_rate' ? parseFloat(value) || 0 : parseFloat(prev.pay_rate) || 0;
        const hours = field === 'hours' ? parseFloat(value) || 0 : parseFloat(prev.hours) || 0;
        updated.total_amount = rate * hours;
      }

      return updated;
    });
  };

  const handleClose = () => {
    if (autocompleteInitialized.current) {
      destroyPlaceAutocomplete(containerId);
      autocompleteInitialized.current = false;
    }
    setFullJobData(null);
    setDataLoaded(false);
    setAutocompleteReady(false);
    addressValueRef.current = '';
    onClose();
  };

  if (!job) return null;

  const getStatusLabel = (status) => {
    const statusMap = {
      'PENDING_APPROVAL': 'Pending Approval',
      'SEEKING_APPLICANTS': 'Seeking Applicants',
      'OFFER_SENT': 'Offer Sent',
      'HIRED': 'Hired',
      'PENDING_COMPLETION': 'Pending Completion',
      'RELEASE_PAYMENT': 'Release Payment',
      'PAID_COMPLETED': 'Paid/Completed',
      'CANCELLED': 'Cancelled'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'PENDING_APPROVAL': 'bg-orange-100 text-orange-700 border-orange-300',
      'SEEKING_APPLICANTS': 'bg-purple-100 text-purple-700 border-purple-300',
      'OFFER_SENT': 'bg-amber-100 text-amber-700 border-amber-300',
      'HIRED': 'bg-green-100 text-green-700 border-green-300',
      'PENDING_COMPLETION': 'bg-indigo-100 text-indigo-700 border-indigo-300',
      'RELEASE_PAYMENT': 'bg-cyan-100 text-cyan-700 border-cyan-300',
      'PAID_COMPLETED': 'bg-teal-100 text-teal-700 border-teal-300',
      'CANCELLED': 'bg-red-100 text-red-700 border-red-300'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  // Role options that match common database values
  const roleOptions = [
    { value: '', label: 'Select Role' },
    { value: 'photographer', label: 'Photographer' },
    { value: 'videographer', label: 'Videographer' },
    { value: 'photo_and_video', label: 'Photo & Video' },
    { value: 'editor', label: 'Editor' },
    { value: 'assistant', label: 'Assistant' },
    { value: 'drone_operator', label: 'Drone Operator' },
    { value: 'second_shooter', label: 'Second Shooter' },
    { value: 'photo_booth', label: 'Photo Booth Operator' },
    { value: 'photo_booth_operator', label: 'Photo Booth Operator' },
    { value: 'lead_photographer', label: 'Lead Photographer' },
    { value: 'lead_videographer', label: 'Lead Videographer' },
  ];

  // Command Deck dark styling helpers (visual only)
  const cdInput = {
    width: '100%', padding: '11px 13px', border: '1px solid rgba(255,255,255,.14)',
    borderRadius: 11, background: 'rgba(255,255,255,.04)', color: '#fff',
    fontFamily: 'Work Sans, sans-serif', fontSize: '13.5px', outline: 'none'
  };
  const cdSelect = { ...cdInput, appearance: 'none', WebkitAppearance: 'none' };
  const cdFocus = (e) => { e.target.style.borderColor = 'rgba(40,192,208,.55)'; };
  const cdBlur = (e) => { e.target.style.borderColor = 'rgba(255,255,255,.14)'; };
  const cdLabel = { fontSize: '11.5px', color: 'rgba(255,255,255,.6)', fontFamily: 'Work Sans, sans-serif' };
  const cdCard = { background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 16 };
  const cdHeading = { fontFamily: 'Poppins, sans-serif', color: 'rgba(255,255,255,.85)' };
  const cdOpt = { background: '#12162A', color: '#fff' };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        style={{
          background: '#12162A', border: '1px solid rgba(255,255,255,.12)', borderRadius: 22,
          boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)', color: '#fff', fontFamily: 'Work Sans, sans-serif'
        }}
      >
        <DialogHeader
          style={{
            margin: '-24px -24px 0', padding: '24px 26px 18px',
            background: 'linear-gradient(120deg,#572B7C,#78287C)',
            borderRadius: '22px 22px 0 0'
          }}
        >
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl flex items-center gap-2" style={{ ...cdHeading, fontWeight: 800, color: '#fff' }}>
              <FileText className="w-5 h-5" style={{ color: '#28C0D0' }} />
              Edit Job
            </DialogTitle>
            {formData.status && (
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(formData.status)}`}>
                {getStatusLabel(formData.status)}
              </span>
            )}
          </div>
          <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,.7)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
            Job ID: {job.mysql_job_id || job.id?.substring(0, 8)}
          </p>
        </DialogHeader>

        {loadingJobData ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin" style={{ color: '#28C0D0' }} />
            <span className="ml-3" style={{ color: 'rgba(255,255,255,.6)' }}>Loading job details...</span>
          </div>
        ) : (
          <div className="space-y-6 mt-4">
            {/* Basic Info Section */}
            <div className="p-4" style={cdCard}>
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={cdHeading}>
                <Briefcase className="w-4 h-4" style={{ color: '#28C0D0' }} />
                Basic Information
              </h3>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm" style={cdLabel}>Job Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="e.g., Wedding Photographer Needed"
                    className="mt-1"
                    style={cdInput}
                    onFocus={cdFocus}
                    onBlur={cdBlur}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm" style={cdLabel}>Job Type (Category)</Label>
                    <select
                      value={formData.job_type}
                      onChange={(e) => handleChange('job_type', e.target.value)}
                      className="mt-1"
                      style={cdSelect}
                      onFocus={cdFocus}
                      onBlur={cdBlur}
                    >
                      <option value="" style={cdOpt}>Select Category</option>
                      <option value="WEDDINGS" style={cdOpt}>Weddings</option>
                      <option value="CORPORATE" style={cdOpt}>Corporate</option>
                      <option value="EVENTS" style={cdOpt}>Events</option>
                      <option value="REAL_ESTATE" style={cdOpt}>Real Estate</option>
                      <option value="PORTRAIT" style={cdOpt}>Portrait</option>
                      <option value="COMMERCIAL" style={cdOpt}>Commercial</option>
                      <option value="OTHERS" style={cdOpt}>Other</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm" style={cdLabel}>Role Required</Label>
                    <select
                      value={formData.role}
                      onChange={(e) => handleChange('role', e.target.value)}
                      className="mt-1"
                      style={cdSelect}
                      onFocus={cdFocus}
                      onBlur={cdBlur}
                    >
                      {roleOptions.map(opt => (
                        <option key={opt.value} value={opt.value} style={cdOpt}>{opt.label}</option>
                      ))}
                    </select>
                    {formData.role && !roleOptions.find(o => o.value === formData.role) && (
                      <p className="text-xs mt-1" style={{ color: '#FCD34D' }}>
                        Current value: "{formData.role}" (not in dropdown)
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-sm" style={cdLabel}>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Detailed job description..."
                    rows={6}
                    className="mt-1 whitespace-pre-wrap"
                    style={{ ...cdInput, resize: 'vertical', lineHeight: 1.5 }}
                    onFocus={cdFocus}
                    onBlur={cdBlur}
                  />
                  <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,.4)' }}>Line breaks will be preserved</p>
                </div>
              </div>
            </div>

            {/* Date Section */}
            <div className="p-4" style={cdCard}>
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={cdHeading}>
                <Calendar className="w-4 h-4" style={{ color: '#28C0D0' }} />
                Date
              </h3>

              <div>
                <Label className="text-sm" style={cdLabel}>Job Date</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  className="mt-1 max-w-xs"
                  style={{ ...cdInput, colorScheme: 'dark' }}
                  onFocus={cdFocus}
                  onBlur={cdBlur}
                />
              </div>
            </div>

            {/* Location Section */}
            <div className="p-4" style={cdCard}>
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={cdHeading}>
                <MapPin className="w-4 h-4" style={{ color: '#28C0D0' }} />
                Location
              </h3>

              <div>
                <Label className="text-sm" style={cdLabel}>Address *</Label>
                <p className="text-xs mb-2" style={{ color: 'rgba(255,255,255,.4)' }}>Start typing to search for an address with Google Places</p>

                {/* Google Places Autocomplete container */}
                <div id={containerId} className="mt-1"></div>

                {/* Fallback input if Google Places isn't working */}
                {dataLoaded && !autocompleteReady && (
                  <Input
                    value={formData.location_address}
                    onChange={(e) => handleChange('location_address', e.target.value)}
                    placeholder="Enter address..."
                    className="mt-1"
                    style={cdInput}
                    onFocus={cdFocus}
                    onBlur={cdBlur}
                  />
                )}

                {formData.location_lat && formData.location_lng && (
                  <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,.5)' }}>
                    <span style={{ background: 'rgba(40,192,208,.16)', color: '#28C0D0', padding: '1px 8px', borderRadius: 6 }}>
                      Geocoded
                    </span>
                    <span>Lat: {parseFloat(formData.location_lat).toFixed(6)}, Lng: {parseFloat(formData.location_lng).toFixed(6)}</span>
                  </div>
                )}

                {!formData.location_lat && formData.location_address && (
                  <div className="mt-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" style={{ color: '#FCD34D' }} />
                    <span className="text-xs" style={{ color: '#FCD34D' }}>
                      No geocode data. Select an address from the dropdown to ensure correct location.
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Pricing Section */}
            <div className="p-4" style={cdCard}>
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={cdHeading}>
                <DollarSign className="w-4 h-4" style={{ color: '#28C0D0' }} />
                Pricing
              </h3>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm" style={cdLabel}>Pay Rate ($/hr)</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,.5)' }}>$</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.pay_rate}
                      onChange={(e) => handleChange('pay_rate', e.target.value)}
                      placeholder="110"
                      className="pl-7"
                      style={{ ...cdInput, paddingLeft: 28 }}
                      onFocus={cdFocus}
                      onBlur={cdBlur}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm" style={cdLabel}>Hours</Label>
                  <div className="relative mt-1">
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.hours}
                      onChange={(e) => handleChange('hours', e.target.value)}
                      placeholder="5"
                      style={cdInput}
                      onFocus={cdFocus}
                      onBlur={cdBlur}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'rgba(255,255,255,.4)' }}>hrs</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm" style={cdLabel}>Total Amount</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,.5)' }}>$</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.total_amount}
                      onChange={(e) => handleChange('total_amount', e.target.value)}
                      placeholder="Calculated automatically"
                      className="pl-7"
                      style={{ ...cdInput, paddingLeft: 28 }}
                      onFocus={cdFocus}
                      onBlur={cdBlur}
                    />
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,.4)' }}>Auto-calculated from rate × hours</p>
                </div>
              </div>
            </div>

            {/* Additional Info Section */}
            <div className="p-4" style={cdCard}>
              <h3 className="text-sm font-semibold mb-4" style={cdHeading}>Additional Information</h3>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm" style={cdLabel}>Requirements / Special Instructions</Label>
                  <Textarea
                    value={formData.requirements}
                    onChange={(e) => handleChange('requirements', e.target.value)}
                    placeholder="Any special requirements for this job..."
                    rows={3}
                    className="mt-1"
                    style={{ ...cdInput, resize: 'vertical', lineHeight: 1.5 }}
                    onFocus={cdFocus}
                    onBlur={cdBlur}
                  />
                </div>

              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,.08)' }}>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={loading}
                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: '#fff' }}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                style={{ background: '#28C0D0', color: '#0C1120', fontWeight: 700 }}
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditJobModal;
